import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Grid,
  TextField,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Box,
  makeStyles,
} from "@material-ui/core";
import { Delete, Publish } from "@material-ui/icons";
import { DropzoneArea } from "material-ui-dropzone";
import { get, set } from "@totalsoft/change-tracking-react";
import { emptyString } from "constants/defaultValues";
import SectionTitle from "features/common/components/SectionTitle";
import { addToast } from "components/notifications";
import styles from "../styles";
import { mimeTypes } from "../constants";

const useStyles = makeStyles(styles);

function ProfileDataCardCIAM({ userProfileLens }) {
  const { t } = useTranslation();
  const [inputImageToBase64, setInputImageToBase64] = useState(null);
  const classes = useStyles();

  const handleChangePicture = useCallback(
    (files) => {
      if (files.length > 0) {
        (userProfileLens.picture |> set)(files[0]);
        let reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
          setInputImageToBase64(reader.result);
        };
      }
    },
    [userProfileLens.picture]
  );

  const handleRemovePicture = useCallback(() => {
    (userProfileLens.picture |> set)(null);
  }, [userProfileLens.picture]);

  const getUploadPicture = useCallback(() => {
    const maxSize = get(userProfileLens.maxSizeInKB);
    const allowedFileExtensions = Object.keys(mimeTypes)
      .filter((key) => get(userProfileLens.allowedFileExtensions)?.split(",").includes(mimeTypes[key]))
      .join(", ");

    return (
      <>
        <Publish />
        {maxSize && (
          <Typography variant="body2" className={classes.dropzoneText}>
            {t("General.MaxFileSize", {
              maxFileSize: maxSize,
            })}
          </Typography>
        )}
        {allowedFileExtensions && (
          <Typography variant="body2" className={classes.dropzoneText}>
            {t("General.SupportedExtensions", {
              supportedExtensions: allowedFileExtensions,
            })}
          </Typography>
        )}
      </>
    );
  }, [classes.dropzoneText, t, userProfileLens.allowedFileExtensions, userProfileLens.maxSizeInKB]);

  const handleDropRejected = useCallback(() => {
    addToast(t("General.FileTypeOrFileSizeNotAccepted"), "error");
  }, [t]);

  const getFileLimitExceedMessage = useCallback(
    (filesLimit) => {
      addToast(t("General.MaximumFilesToDrop", { filesLimit: filesLimit }), "error");
    },
    [t]
  );

  return (
    <Card>
      <CardContent>
        <SectionTitle title={t("ProfilePage.ProfileData")} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} lg={4}>
            <TextField
              id={"user_email"}
              label={t("ProfilePage.Email")}
              value={(userProfileLens.email |> get) || emptyString}
              fullWidth
              disabled={true}
            />
          </Grid>
          <Grid item xs={12}>
            <Box width="fit-content">
              <Box display="flex" alignItems="flex-end">
                <Box flexGrow={1}>
                  <Typography gutterBottom variant="subtitle2">
                    {t("ProfilePage.ProfilePhoto")}
                  </Typography>
                </Box>
                {(userProfileLens.picture |> get) == null ? (
                  <></>
                ) : (
                  <>
                    <Box>
                      <Tooltip title={t("ProfilePage.DeletePhoto")}>
                        <IconButton color="primary" onClick={handleRemovePicture}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Box>
              <Box boxShadow={1}>
                {(userProfileLens.picture |> get) == null ? (
                  <DropzoneArea
                    acceptedFiles={[userProfileLens.allowedFileExtensions |> get]}
                    dropzoneText={t("ProfilePage.AddPicture")}
                    Icon={getUploadPicture}
                    onChange={handleChangePicture}
                    filesLimit={1}
                    maxFileSize={(userProfileLens.maxSizeInKB |> get) * 1024}
                    onDropRejected={handleDropRejected}
                    getFileLimitExceedMessage={getFileLimitExceedMessage}
                    showAlerts={false}
                  />
                ) : (
                  <img
                    src={inputImageToBase64 ? inputImageToBase64 : userProfileLens.picture |> get}
                    className={classes.uploadedPhoto}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

ProfileDataCardCIAM.propTypes = {
  userProfileLens: PropTypes.object.isRequired,
};

export default ProfileDataCardCIAM;
